/**
 * Inline rename popover for Lumyn Live sessions.
 *
 * Click the pencil → small floating editor with input + save/cancel.
 * Stops click propagation so the parent card doesn't open the session.
 *
 * Extracted verbatim from LiveHostPage.jsx during the Phase 2 refactor
 * (no behaviour change).
 */
import { useState, useRef, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { Button } from '../../ui/button';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';

export function RenamePopover({ session, onSaved, onClose }) {
  const { patch } = useApi();
  const [value, setValue] = useState(session.quiz_title || '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Session name cannot be empty');
      return;
    }
    if (trimmed === session.quiz_title) { onClose(); return; }
    setBusy(true);
    try {
      const updated = await patch(`/live/sessions/${session.session_id}`, { title: trimmed });
      toast.success('Session renamed');
      onSaved(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="absolute z-20 right-0 top-full mt-1 w-72 rounded-lg border border-border bg-popover shadow-lg p-2"
      data-testid="rename-popover"
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') onClose();
        }}
        maxLength={160}
        disabled={busy}
        className="w-full text-sm px-2 py-1.5 rounded border border-border bg-background outline-none focus:ring-2 focus:ring-[var(--lumyn-live)]/40"
        data-testid="rename-input"
        placeholder="Session name"
      />
      <div className="mt-2 flex items-center justify-end gap-1">
        <Button
          variant="ghost" size="sm"
          onClick={onClose}
          disabled={busy}
          data-testid="rename-cancel"
          className="h-7 px-2"
        >
          <X className="w-3.5 h-3.5 mr-1" />Cancel
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          data-testid="rename-save"
          className="h-7 px-2 bg-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/90 text-white"
        >
          <Check className="w-3.5 h-3.5 mr-1" />Save
        </Button>
      </div>
    </div>
  );
}
